import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeftRight } from 'lucide-react'
import { AuthPageContainer } from '@/components/shared/layouts'
import { Button } from '@/components/ui/button'
import TabsComponent, { Tab } from '@/components/shared/tabs'
import {
  InvoicesTable,
  SentInvoicesTable,
  ClientInvoicesTabs,
} from '@/components/tables/invoices'
import { useGetInvoiceStats } from '@/hooks/invoices'
import { useAuthStore } from '@/store/auth'
import { UserRole } from '@depanneo/types'
import { requiredRoles } from '@/shared/utils/require-roles'

type OperatorTabValue = 'todo' | 'sent'
type ClientTabValue = 'topay' | 'overdue' | 'paid'
type InvoiceTabValue = OperatorTabValue | ClientTabValue

export type InvoicesSearch = {
  tab: InvoiceTabValue
}

const VALID_TABS: InvoiceTabValue[] = [
  'todo',
  'sent',
  'topay',
  'overdue',
  'paid',
]

export const Route = createFileRoute('/_authenticated/org/$orgId/invoices/')({
  component: InvoicesPage,
  validateSearch: (search: Record<string, unknown>): InvoicesSearch => {
    const tab = search.tab as InvoiceTabValue
    return {
      tab: VALID_TABS.includes(tab) ? tab : 'todo',
    }
  },
  // The roles holding Invoice read (the « Factures » sidebar entry's); VIEWER and
  // TECHNICIAN get the 403 page instead of an empty invoice screen (#2421).
  beforeLoad: () => {
    requiredRoles([UserRole.FACILITY_MANAGER, UserRole.ADMIN])
  },
})

function InvoicesPage() {
  const { orgId } = Route.useParams()
  const role = useAuthStore((state) => state.role)
  const isSuperAdmin = role === UserRole.SUPER_ADMIN

  return (
    <AuthPageContainer
      headerTitle='Factures'
      actions={
        isSuperAdmin ? (
          <Button asChild variant='outline'>
            <Link
              to='/org/$orgId/invoices/reconciliation'
              params={{ orgId }}
              search={{ tab: 'new' }}
            >
              <ArrowLeftRight className='size-4' />
              Rapprochement
            </Link>
          </Button>
        ) : undefined
      }
    >
      {isSuperAdmin ? <OperatorInvoices /> : <ClientInvoicesTabs />}
    </AuthPageContainer>
  )
}

function OperatorInvoices() {
  const { tab } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: stats } = useGetInvoiceStats()

  // Client tab values can linger in the URL after a role switch — coerce.
  const operatorTab: OperatorTabValue = tab === 'sent' ? 'sent' : 'todo'

  const tabs: Tab<OperatorTabValue>[] = [
    {
      name: 'À faire',
      value: 'todo',
      content: <InvoicesTable />,
      details: { count: stats?.counts.draft, isPrimary: true },
    },
    {
      name: 'Envoyées',
      value: 'sent',
      content: <SentInvoicesTable />,
      details: { count: stats?.counts.sent, isPrimary: true },
    },
  ]

  const handleTabChange = (value: OperatorTabValue) => {
    navigate({
      search: { tab: value },
    })
  }

  return (
    <TabsComponent
      tabs={tabs}
      defaultValue='todo'
      value={operatorTab}
      onTabChange={handleTabChange}
    />
  )
}
